import { createContext, useContext } from 'react';

export type FirstDayOfWeek =
  | 'sun'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat';

export interface DateFormatContextValue {
  /**
   * Overrides the calendar's first day of week independently of the locale, so
   * approximating a date format via a locale doesn't also change the week start.
   */
  firstDayOfWeek?: FirstDayOfWeek;
  /**
   * Separator to render in place of the locale's between editable date
   * segments, so a configured format like `MM-dd-yyyy` keeps its dashes.
   */
  separator?: string;
}

const DateFormatContext = createContext<DateFormatContextValue>({});

export const DateFormatProvider = DateFormatContext.Provider;

export function useDateFormat(): DateFormatContextValue {
  return useContext(DateFormatContext);
}

// Date separators the mapped locales (en-US/en-GB/en-CA/de-DE) emit. Matching
// only these leaves a datetime input's time colons and date/time joiner alone.
const LOCALE_DATE_SEPARATOR = /^[/.-]$/;

/**
 * Rewrite a date input's literal separator segment with the configured
 * `separator`, so a format like `MM-dd-yyyy` keeps its dashes even though the
 * approximating locale would render `/`. Editable segments, time colons and the
 * date/time joiner are returned untouched.
 */
export function applyDateSeparator<T extends { type: string; text: string }>(
  segment: T,
  separator: string | undefined,
): T {
  if (
    segment.type === 'literal' &&
    separator !== undefined &&
    LOCALE_DATE_SEPARATOR.test(segment.text)
  ) {
    return { ...segment, text: separator };
  }
  return segment;
}
